import React, { useState } from 'react';
import { HiOutlinePlus, HiOutlineArrowPath, HiOutlineMagnifyingGlass } from 'react-icons/hi2';
import { HiOutlinePencilAlt, HiOutlineTrash, HiOutlineExternalLink, HiOutlineViewGrid, HiOutlineViewList } from 'react-icons/hi';
import { useNavigate } from 'react-router-dom';
import { useSearch, useTourCategories } from '../../../../hooks';
import { Alert, Modal } from '../../../commons';
import { CategoryProps, DAV_MAIN_DOMAIN } from '../../../../constants';
import { DAV_APIS } from '../../../../adapters';
import DashboardLoader from '../../sections/Loader/DashboardLoader';
import { Card } from '../../../ui/Card';
import { Button } from '../../../ui/Button';

const Categories = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('' as string);
  const [selectedCategory, setSelectedCategory] = useState('' as string);
  const [refresh, setRefresh] = useState<number | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [layout, setLayout] = useState<'grid' | 'list'>('grid');
  const { categories, loading } = useTourCategories(refresh);
  const navigate = useNavigate();

  const categoriesResults = useSearch(categories, searchTerm);

  const isList = layout === 'list';

  const handleDeleteCategory = async () => {
    setIsLoading(true);
    try {
      const res = await DAV_APIS.country.deleteCategoryById(selectedCategory as string);
      if (res.status === 204) {
        Alert({ message: 'Deleted Successfully', variant: 'success' });
      }
      setIsLoading(false);
      setShowModal(false);
      setRefresh(Math.random());
    } catch (error) {
      setIsLoading(false);
      setShowModal(false);
      Alert({ message: 'Failed to delete category', variant: 'error' });
    }
  };

  const onDeleteHandler = (categoryId: string) => {
    setSelectedCategory(categoryId);
    setShowModal(true);
  };

  const onEditHandler = (categoryId: string) => {
    navigate(`/dashboard/categories/edit?category=${categoryId}`);
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className='flex flex-col items-center justify-center py-20 animate-pulse'>
          <DashboardLoader />
        </div>
      );
    }

    if (categoriesResults?.length > 0) {
      return (
        <div className={`grid gap-6 ${isList ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2 xl:grid-cols-3'}`}>
          {categoriesResults.map((category: CategoryProps, index: number) => (
            <Card 
              key={category.id} 
              variant='glass' 
              hoverable
              className={`
                group overflow-hidden border-none shadow-lg shadow-gray-200/50 dark:shadow-none 
                transition-all duration-300
                ${isList ? 'h-full sm:h-56' : 'h-full flex flex-col'}
              `}
              style={{ animationDelay: `${index * 50}ms` }}
            >
              <div className={`flex flex-col ${isList ? 'sm:flex-row h-full' : 'h-full'}`}>
                <div className={`relative overflow-hidden ${isList ? 'w-full sm:w-72 h-48 sm:h-full flex-shrink-0' : 'aspect-[16/10]'}`}>
                  <img 
                    src={category.tourCategoryImage} 
                    alt={category.name} 
                    className='w-full h-full object-cover transition-transform duration-700 group-hover:scale-110'
                  />
                  <div className='absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-80 group-hover:opacity-100 transition-opacity duration-500' />
                  
                  {/* Country Badge */}
                  <div className='absolute top-3 right-3 z-10'>
                    <span className='px-2.5 py-1 rounded-full bg-white/20 backdrop-blur-md text-[10px] font-bold text-white uppercase tracking-widest border border-white/20 shadow-sm'>
                      {category.country.name}
                    </span>
                  </div>

                  <div className='absolute bottom-4 left-4 right-4 text-white'>
                    <h3 className='text-lg font-bold flex items-center gap-2 drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]'>
                      {category.name}
                      <a 
                        href={`${DAV_MAIN_DOMAIN}/${category.country.slug}/${category.slug}`} 
                        target='_blank' 
                        rel='noreferrer'
                        className='p-1.5 rounded-full bg-white/20 backdrop-blur-md opacity-0 group-hover:opacity-100 transition-opacity translate-y-2 group-hover:translate-y-0 duration-300'
                      >
                        <HiOutlineExternalLink className='w-4 h-4 text-white' />
                      </a>
                    </h3>
                  </div>
                </div>
                
                <div className='flex flex-col flex-1 p-5 bg-white/50 dark:bg-gray-800/10 min-w-0'>
                  {category.description && (
                    <p className='text-sm text-gray-600 dark:text-gray-400 line-clamp-2 leading-relaxed mb-4'>
                      {category.description}
                    </p>
                  )}

                  <div className='flex items-center justify-between pt-4 border-t border-gray-100 dark:border-gray-700/50 mt-auto'>
                    <div className='flex items-center gap-2'>
                      <button
                        type='button'
                        onClick={() => onEditHandler(category.id)}
                        className='p-2.5 rounded-xl bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-primary-50 hover:text-primary-600 dark:hover:bg-primary-900/20 dark:hover:text-primary-400 transition-all border border-transparent hover:border-primary-200'
                        title='Edit Category'
                      >
                        <HiOutlinePencilAlt className='w-5 h-5' />
                      </button>
                      <button
                        type='button'
                        onClick={() => onDeleteHandler(category.id)}
                        className='p-2.5 rounded-xl bg-rose-50 dark:bg-rose-900/10 text-rose-600 dark:text-rose-400 hover:bg-rose-100 hover:text-rose-700 dark:hover:bg-rose-900/30 transition-all border border-transparent hover:border-rose-200'
                        title='Delete Category'
                      >
                        <HiOutlineTrash className='w-5 h-5' />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      );
    }

    return (
      <Card variant='glass' className='py-20 flex flex-col items-center justify-center text-center'>
        <div className='w-20 h-20 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4'>
          <HiOutlineMagnifyingGlass className='w-10 h-10 text-gray-400' />
        </div>
        <h3 className='text-lg font-bold text-gray-900 dark:text-white'>No categories found</h3>
        <p className='text-gray-500 dark:text-gray-400 max-w-sm mt-1 px-6'>
          {searchTerm ? 
            `We couldn't find any categories matching "${searchTerm}".`
            : `There are currently no tour categories configured.`}
        </p>
      </Card>
    );
  };

  return (
    <div className='space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700'>
      {/* Page Header */}
      <div className='flex flex-col md:flex-row md:items-center justify-between gap-4'>
        <div>
          <h1 className='text-3xl font-extrabold text-gray-900 dark:text-white tracking-tight'>
            Tour Categories
          </h1>
          <p className='text-gray-500 dark:text-gray-400 mt-1 max-w-2xl'>
            Group your safari experiences by themes, difficulty, or regional interests.
          </p>
        </div>
        <div className='flex items-center gap-3'>
          <Button
            variant='primary'
            onClick={() => navigate('/dashboard/categories/new')}
            leftIcon={<HiOutlinePlus className='w-5 h-5' />}
          >
            Create Category
          </Button>
        </div>
      </div>

      {/* Controls Bar */}
      <Card variant='glass' className='p-4 border-none shadow-lg shadow-gray-200/40 dark:shadow-none'>
        <div className='flex items-center gap-4'>
          {/* Search */}
          <div className='relative flex-1'>
            <div className='absolute left-4 top-1/2 -translate-y-1/2 text-gray-400'>
              <HiOutlineMagnifyingGlass className='w-5 h-5' />
            </div>
            <input
              type='text'
              placeholder='Search categories...'
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className='
                w-full pl-11 pr-4 py-2.5 bg-white/50 dark:bg-gray-800/50 
                border border-gray-200 dark:border-gray-700 rounded-xl
                focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500
                placeholder:text-gray-400 dark:placeholder:text-gray-500
                transition-all duration-200
              '
            />
          </div>

          <div className='flex items-center gap-3 ml-auto'>
            {/* Layout Toggle */}
            <div className='flex items-center p-1 bg-gray-100 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700/50'>
              <button
                type='button'
                onClick={() => setLayout('grid')}
                className={`
                  p-1.5 rounded-md transition-all duration-200
                  ${layout === 'grid' ?
                    'bg-white dark:bg-gray-700 text-primary-600 dark:text-primary-400 shadow-sm'
                    : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'
                  }
                `}
                title='Grid View'
              >
                <HiOutlineViewGrid className='w-5 h-5' />
              </button>
              <button
                type='button'
                onClick={() => setLayout('list')}
                className={`
                  p-1.5 rounded-md transition-all duration-200
                  ${layout === 'list' ?
                    'bg-white dark:bg-gray-700 text-primary-600 dark:text-primary-400 shadow-sm'
                    : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'
                  }
                `}
                title='List View'
              >
                <HiOutlineViewList className='w-5 h-5' />
              </button>
            </div>

            {/* Refresh Button */}
            <button
              type='button'
              onClick={() => setRefresh(Math.random())}
              disabled={loading}
              className={`
                p-2.5 rounded-xl border border-gray-200 dark:border-gray-700
                bg-white/50 dark:bg-gray-800/50 text-gray-600 dark:text-gray-400
                hover:bg-primary-50 hover:text-primary-600 dark:hover:bg-primary-900/20 dark:hover:text-primary-400
                transition-all duration-200 shadow-sm
                ${loading ? 'opacity-50 cursor-not-allowed' : 'active:scale-95'}
              `}
              title='Refresh list'
            >
              <HiOutlineArrowPath className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </Card>

      {renderContent()}

      {/* Delete Confirmation Modal */}
      <Modal
        size='sm'
        open={showModal}
        onClose={() => setShowModal(false)}
        onSuccess={handleDeleteCategory}
        loading={isLoading}
        successText='Delete Category'
        loadingText='Deleting...'
      >
        <div className='space-y-3'>
          <h2 className='text-xl font-bold text-gray-900 dark:text-white'>Remove Category</h2>
          <p className='text-gray-600 dark:text-gray-400 leading-relaxed'>
            Are you sure you want to delete this category? Tours using this category will need to be reassigned. This action cannot be undone.
          </p>
        </div>
      </Modal>
    </div>
  );
};

export default Categories;
